/**
 * Wspólne klocki stron-list kartotek (Kierowcy, Pojazdy) — Admin 1.437.0.
 *
 * - `HeroKpi`         — kolorowy kafel w hero; klikalny = szybki filtr (aktywny ma obwódkę).
 * - `HeroHealthBar`   — pasek „kondycji" (ważne / wkrótce / wygasłe / brak danych), segmenty klikalne.
 * - `StatusTabs`      — zakładki statusu, każda we własnym kolorze.
 * - `FilterSelect`    — lista rozwijana z ikoną; z ustawionym filtrem podświetla się, żeby było go widać.
 * - `QuickChip`       — kolorowy przełącznik w wierszu szybkich filtrów (typ, rodzaj, zakres).
 * - `Segmented`       — przełącznik kilku opcji (np. grupowanie) zamiast przycisku „w kółko".
 * - `SearchBox`       — wyszukiwarka z przyciskiem czyszczenia i skrótem „/".
 * - `ActiveFilterBar` — aktywne filtry jako chipy do zdjęcia jednym kliknięciem + „Wyczyść".
 * - `GroupHeader`     — nagłówek sekcji grupowania: zwijany, z udziałem w całości.
 * - `usePersistentState`, `useCollapsedGroups` — ustawienia widoku zapamiętane w przeglądarce.
 *
 * Hero jest zawsze ciemny, więc kafle używają kolorów hex; reszta ma warianty `dark:`
 * (motyw ciemny panelu — klasa `.dark` na <html>).
 */
import { useCallback, useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { ChevronDown, Search, X, FilterX } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

import { Lista } from '@/shared/components/ui/Lista';
// ─── Kolory ───────────────────────────────────────────────────────────────────
// Pełne nazwy klas (Tailwind musi je znaleźć w źródle — nie składamy ich z kawałków).

export type Tone =
  | 'slate' | 'blue' | 'emerald' | 'red' | 'amber' | 'rose' | 'sky' | 'purple'
  | 'orange' | 'violet' | 'indigo' | 'teal' | 'cyan' | 'fuchsia' | 'lime';

interface ToneCls { dot: string; solid: string; soft: string; count: string; icon: string; picked: string }

export const TONE: Record<Tone, ToneCls> = {
  slate:   { dot: 'bg-slate-400',   solid: 'bg-foreground text-background',                  soft: 'text-muted-foreground hover:bg-muted hover:text-foreground',                                           count: 'bg-muted text-muted-foreground',                                             icon: 'text-slate-500 dark:text-slate-400',     picked: 'border-slate-400/60 bg-slate-100 text-slate-800 dark:bg-slate-500/15 dark:text-slate-100 dark:border-slate-400/40' },
  blue:    { dot: 'bg-blue-500',    solid: 'bg-blue-600 text-white shadow-blue-600/30',       soft: 'text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10',                     count: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',         icon: 'text-blue-600 dark:text-blue-400',       picked: 'border-blue-400/60 bg-blue-50 text-blue-800 dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/40' },
  emerald: { dot: 'bg-emerald-500', solid: 'bg-emerald-600 text-white shadow-emerald-600/30', soft: 'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/10',         count: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', icon: 'text-emerald-600 dark:text-emerald-400', picked: 'border-emerald-400/60 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/40' },
  red:     { dot: 'bg-red-500',     solid: 'bg-red-600 text-white shadow-red-600/30',         soft: 'text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10',                         count: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',             icon: 'text-red-600 dark:text-red-400',         picked: 'border-red-400/60 bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/40' },
  amber:   { dot: 'bg-amber-500',   solid: 'bg-amber-500 text-white shadow-amber-500/30',     soft: 'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10',                 count: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',     icon: 'text-amber-600 dark:text-amber-400',     picked: 'border-amber-400/60 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/40' },
  rose:    { dot: 'bg-rose-500',    solid: 'bg-rose-600 text-white shadow-rose-600/30',       soft: 'text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10',                     count: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',         icon: 'text-rose-600 dark:text-rose-400',       picked: 'border-rose-400/60 bg-rose-50 text-rose-800 dark:bg-rose-500/10 dark:text-rose-200 dark:border-rose-500/40' },
  sky:     { dot: 'bg-sky-500',     solid: 'bg-sky-600 text-white shadow-sky-600/30',         soft: 'text-sky-700 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-500/10',                         count: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',             icon: 'text-sky-600 dark:text-sky-400',         picked: 'border-sky-400/60 bg-sky-50 text-sky-800 dark:bg-sky-500/10 dark:text-sky-200 dark:border-sky-500/40' },
  purple:  { dot: 'bg-purple-500',  solid: 'bg-purple-600 text-white shadow-purple-600/30',   soft: 'text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-500/10',             count: 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300', icon: 'text-purple-600 dark:text-purple-400',   picked: 'border-purple-400/60 bg-purple-50 text-purple-800 dark:bg-purple-500/10 dark:text-purple-200 dark:border-purple-500/40' },
  orange:  { dot: 'bg-orange-500',  solid: 'bg-orange-600 text-white shadow-orange-600/30',   soft: 'text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-500/10',             count: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300', icon: 'text-orange-600 dark:text-orange-400',   picked: 'border-orange-400/60 bg-orange-50 text-orange-800 dark:bg-orange-500/10 dark:text-orange-200 dark:border-orange-500/40' },
  violet:  { dot: 'bg-violet-500',  solid: 'bg-violet-600 text-white shadow-violet-600/30',   soft: 'text-violet-700 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-500/10',             count: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300', icon: 'text-violet-600 dark:text-violet-400',   picked: 'border-violet-400/60 bg-violet-50 text-violet-800 dark:bg-violet-500/10 dark:text-violet-200 dark:border-violet-500/40' },
  indigo:  { dot: 'bg-indigo-500',  solid: 'bg-indigo-600 text-white shadow-indigo-600/30',   soft: 'text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10',             count: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300', icon: 'text-indigo-600 dark:text-indigo-400',   picked: 'border-indigo-400/60 bg-indigo-50 text-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-200 dark:border-indigo-500/40' },
  teal:    { dot: 'bg-teal-500',    solid: 'bg-teal-600 text-white shadow-teal-600/30',       soft: 'text-teal-700 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-500/10',                     count: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',         icon: 'text-teal-600 dark:text-teal-400',       picked: 'border-teal-400/60 bg-teal-50 text-teal-800 dark:bg-teal-500/10 dark:text-teal-200 dark:border-teal-500/40' },
  cyan:    { dot: 'bg-cyan-500',    solid: 'bg-cyan-600 text-white shadow-cyan-600/30',       soft: 'text-cyan-700 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-500/10',                     count: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',         icon: 'text-cyan-600 dark:text-cyan-400',       picked: 'border-cyan-400/60 bg-cyan-50 text-cyan-800 dark:bg-cyan-500/10 dark:text-cyan-200 dark:border-cyan-500/40' },
  fuchsia: { dot: 'bg-fuchsia-500', solid: 'bg-fuchsia-600 text-white shadow-fuchsia-600/30', soft: 'text-fuchsia-700 hover:bg-fuchsia-50 dark:text-fuchsia-400 dark:hover:bg-fuchsia-500/10',         count: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-300', icon: 'text-fuchsia-600 dark:text-fuchsia-400', picked: 'border-fuchsia-400/60 bg-fuchsia-50 text-fuchsia-800 dark:bg-fuchsia-500/10 dark:text-fuchsia-200 dark:border-fuchsia-500/40' },
  lime:    { dot: 'bg-lime-500',    solid: 'bg-lime-600 text-white shadow-lime-600/30',       soft: 'text-lime-700 hover:bg-lime-50 dark:text-lime-400 dark:hover:bg-lime-500/10',                     count: 'bg-lime-100 text-lime-800 dark:bg-lime-500/15 dark:text-lime-300',         icon: 'text-lime-600 dark:text-lime-400',       picked: 'border-lime-400/60 bg-lime-50 text-lime-900 dark:bg-lime-500/10 dark:text-lime-200 dark:border-lime-500/40' },
};

/** Chip nagłówka grupy (`GroupHeader.cls`) i pasek udziału (`GroupHeader.bar`) w danym tonie. */
export const GROUP_TONE: Record<Tone, { cls: string; bar: string }> = {
  slate:   { cls: 'bg-slate-50 text-slate-600 border-slate-200',       bar: 'bg-slate-400' },
  blue:    { cls: 'bg-blue-50 text-blue-700 border-blue-200',          bar: 'bg-blue-500' },
  emerald: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500' },
  red:     { cls: 'bg-red-50 text-red-700 border-red-200',             bar: 'bg-red-500' },
  amber:   { cls: 'bg-amber-50 text-amber-700 border-amber-200',       bar: 'bg-amber-500' },
  rose:    { cls: 'bg-rose-50 text-rose-700 border-rose-200',          bar: 'bg-rose-500' },
  sky:     { cls: 'bg-sky-50 text-sky-700 border-sky-200',             bar: 'bg-sky-500' },
  purple:  { cls: 'bg-purple-50 text-purple-700 border-purple-200',    bar: 'bg-purple-500' },
  orange:  { cls: 'bg-orange-50 text-orange-700 border-orange-200',    bar: 'bg-orange-500' },
  violet:  { cls: 'bg-violet-50 text-violet-700 border-violet-200',    bar: 'bg-violet-500' },
  indigo:  { cls: 'bg-indigo-50 text-indigo-700 border-indigo-200',    bar: 'bg-indigo-500' },
  teal:    { cls: 'bg-teal-50 text-teal-700 border-teal-200',          bar: 'bg-teal-500' },
  cyan:    { cls: 'bg-cyan-50 text-cyan-700 border-cyan-200',          bar: 'bg-cyan-500' },
  fuchsia: { cls: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', bar: 'bg-fuchsia-500' },
  lime:    { cls: 'bg-lime-50 text-lime-800 border-lime-200',          bar: 'bg-lime-500' },
};

/** Kolory hex do hero (zawsze ciemny) — te same odcienie co `TONE`. */
export const HEX = {
  blue: '#3b82f6', emerald: '#10b981', red: '#ef4444', amber: '#f59e0b', rose: '#f43f5e',
  sky: '#0ea5e9', purple: '#a855f7', orange: '#f97316', violet: '#8b5cf6', indigo: '#6366f1',
  teal: '#14b8a6', cyan: '#06b6d4', fuchsia: '#d946ef', lime: '#84cc16', slate: '#94a3b8',
} as const;

function rgba(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

export function HeroKpi({ icon: Icon, label, value, color, active, onClick, alert, title, compact }: {
  icon: ElementType;
  label: string;
  value: ReactNode;
  /** Kolor hex bańki z ikoną. */
  color: string;
  active?: boolean;
  onClick?: () => void;
  /** Pulsująca kropka — np. „wygasło" > 0. */
  alert?: boolean;
  title?: string;
  /** Niższy kafel (Pulpit, jeden rząd): mniejsza ikona i odstępy. */
  compact?: boolean;
}) {
  const body = (
    <>
      <span className={cn('relative flex shrink-0 items-center justify-center rounded-lg text-white', compact ? 'h-7 w-7' : 'h-8 w-8')}
        style={{ background: `linear-gradient(135deg, ${color}, ${rgba(color, 0.75)})`, boxShadow: `0 4px 14px ${rgba(color, 0.45)}` }}>
        <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        {alert && (
          <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: color }} />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-[#16203a]" style={{ background: color }} />
          </span>
        )}
      </span>
      <span className="min-w-0 text-left">
        <span className="block text-lg font-bold leading-none tabular-nums text-white">{value}</span>
        {/* Zawija się zamiast ucinać — na telefonie (2 kafle w rzędzie) „Krytyczne zdarzenia"
            czy „Nowości w ostatnim wydaniu" były obcinane w pół słowa (Admin 1.454.0). */}
        <span className="mt-0.5 block text-[10px] leading-tight text-white/70">{label}</span>
      </span>
    </>
  );
  const cls = cn('flex items-center rounded-xl ring-1 backdrop-blur-sm transition-all',
    compact ? 'gap-2 px-2.5 py-1.5' : 'gap-2.5 px-3 py-2',
    active ? 'bg-white/25 ring-white/70 shadow-lg' : 'bg-white/10 ring-white/10');
  if (!onClick) return <div className={cls} title={title}>{body}</div>;
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={!!active}
      className={cn(cls, !active && 'hover:bg-white/20 hover:ring-white/30', 'hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white')}>
      {body}
    </button>
  );
}

export interface HealthSegment {
  key: string;
  label: string;
  value: number;
  /** Kolor hex segmentu. */
  color: string;
  active?: boolean;
  onClick?: () => void;
}

/** Pasek „kondycji" w hero: udział każdego stanu, legenda klikalna jak filtr. */
export function HeroHealthBar({ icon: Icon, title, segments, inline }: {
  icon: ElementType; title: string; segments: HealthSegment[];
  /** Jedna wąska linia (ikona + tytuł + pasek + liczby) — do wpięcia w nagłówek hero (Pulpit). */
  inline?: boolean;
}) {
  const total = segments.reduce((n, s) => n + s.value, 0);
  if (!total) return null;
  const pct = (v: number) => (v / total) * 100;
  if (inline) {
    return (
      <div className="inline-flex items-center gap-3 rounded-xl bg-white/[0.07] px-3 py-1.5 ring-1 ring-white/10">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/80">
          <Icon className="h-3.5 w-3.5" />{title}
        </span>
        <div className="flex h-2 w-16 overflow-hidden rounded-full bg-white/10">
          {segments.filter(s => s.value > 0).map(s => (
            <span key={s.key} title={`${s.label}: ${s.value} (${Math.round(pct(s.value))}%)`}
              className="h-full" style={{ width: `${pct(s.value)}%`, background: s.color, minWidth: 3 }} />
          ))}
        </div>
        <span className="flex items-center gap-2.5">
          {segments.map(s => (
            <span key={s.key} className="inline-flex items-center gap-1 text-[11px] text-white" title={`${Math.round(pct(s.value))}%`}>
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="font-bold tabular-nums">{s.value}</span>
              <span className="opacity-70">{s.label}</span>
            </span>
          ))}
        </span>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl bg-white/[0.07] px-3 py-2.5 ring-1 ring-white/10">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/80">
          <Icon className="h-3.5 w-3.5" />{title}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {segments.map(s => {
            const inner = (
              <>
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
                <span className="font-bold tabular-nums">{s.value}</span>
                <span className="opacity-70">{s.label}</span>
                <span className="opacity-50 tabular-nums">{Math.round(pct(s.value))}%</span>
              </>
            );
            const cls = cn('inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] text-white transition-colors',
              s.active ? 'bg-white/25 ring-1 ring-white/60' : 'hover:bg-white/10');
            return s.onClick
              ? <button key={s.key} type="button" onClick={s.onClick} aria-pressed={!!s.active} className={cls}>{inner}</button>
              : <span key={s.key} className={cls}>{inner}</span>;
          })}
        </div>
      </div>
      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-white/10">
        {segments.filter(s => s.value > 0).map(s => (
          <button key={s.key} type="button" onClick={s.onClick} tabIndex={-1}
            title={`${s.label}: ${s.value} (${Math.round(pct(s.value))}%)`}
            className={cn('h-full transition-[filter] hover:brightness-125', s.active && 'brightness-125')}
            style={{ width: `${pct(s.value)}%`, background: s.color, minWidth: 3 }} />
        ))}
      </div>
    </div>
  );
}

// ─── Pasek filtrów ────────────────────────────────────────────────────────────

/** `count` pominięty = licznik ukryty (np. gdy liczba z serwera nie uwzględnia innych filtrów). */
export interface TabDef { value: string; label: string; count?: number; tone: Tone; icon?: ElementType }

export function StatusTabs({ tabs, value, onChange }: { tabs: TabDef[]; value: string; onChange: (v: string) => void }) {
  return (
    <>
      {tabs.map(tab => {
        const t = TONE[tab.tone];
        const on = value === tab.value;
        const Icon = tab.icon;
        return (
          <button key={tab.value} type="button" onClick={() => onChange(tab.value)} aria-pressed={on}
            className={cn('flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold transition-all',
              on ? cn(t.solid, 'shadow-sm') : t.soft)}>
            {Icon
              ? <Icon className="h-3.5 w-3.5 shrink-0" />
              : tab.value !== '' && <span className={cn('h-2 w-2 rounded-full', on ? 'bg-white/90' : t.dot)} />}
            {tab.label}
            {tab.count !== undefined && (
              <span className={cn('inline-flex min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums',
                on ? 'bg-white/20' : t.count)}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}

/** Lista rozwijana z ikoną. Z ustawionym filtrem (`value !== ''`) podświetla się w swoim kolorze. */
export function FilterSelect({ icon: Icon, tone, value, onChange, children, title, neutral }: {
  icon: ElementType;
  tone: Tone;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  title?: string;
  /** Sortowanie — zawsze ma wartość, więc nigdy się nie podświetla. */
  neutral?: boolean;
}) {
  const picked = !neutral && value !== '';
  return (
    <div className="relative" title={title}>
      <Lista value={value} onChange={e => onChange(e.target.value)} ikona={<Icon className="h-4 w-4" />}
        className={cn('appearance-none rounded-xl border py-2 pl-8 pr-8 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30',
          picked ? cn(TONE[tone].picked, 'font-semibold') : 'bg-muted/40')}>
        {children}
      </Lista>
    </div>
  );
}

/** Kolorowy przełącznik w wierszu szybkich filtrów; drugie kliknięcie zdejmuje filtr. */
export function QuickChip({ tone, active, onClick, icon: Icon, label, count }: {
  tone: Tone; active: boolean; onClick: () => void; icon?: ElementType; label: string; count?: number;
}) {
  const t = TONE[tone];
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-bold transition-all',
        active ? cn(t.solid, 'border-transparent shadow-sm') : cn('border-border bg-card hover:border-current', t.soft))}>
      {Icon ? <Icon className="h-3.5 w-3.5" /> : <span className={cn('h-2 w-2 rounded-full', active ? 'bg-white/90' : t.dot)} />}
      {label}
      {count !== undefined && <span className={cn('tabular-nums text-[10px]', active ? 'opacity-80' : 'opacity-60')}>{count}</span>}
    </button>
  );
}

export function Segmented<T extends string>({ value, options, onChange, icon: Icon, title }: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  icon?: ElementType;
  title?: string;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-xl border bg-muted/30 p-1" title={title}>
      {Icon && <Icon className="mx-1 h-3.5 w-3.5 text-muted-foreground" />}
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)} aria-pressed={value === o.value}
          className={cn('h-7 rounded-lg px-2 text-[12px] font-semibold transition-all',
            value === o.value ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Wyszukiwarka: „/" z dowolnego miejsca strony ustawia w niej kursor, Esc czyści. */
export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
      e.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="relative min-w-48 flex-1">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      {/* autoComplete/data-*: menedżery haseł nie wpisują tu e-maila admina (strona Użytkowników ma pola haseł) */}
      <input ref={ref} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        autoComplete="off" data-1p-ignore data-lpignore="true"
        onKeyDown={e => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange(''); } }}
        className="w-full rounded-xl border bg-muted/40 py-2 pl-9 pr-9 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
      {value ? (
        <button type="button" onClick={() => { onChange(''); ref.current?.focus(); }} title="Wyczyść (Esc)"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      ) : (
        <kbd title="Naciśnij „/”, żeby zacząć szukać"
          className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border bg-background px-1.5 font-mono text-[10px] text-muted-foreground sm:block">/</kbd>
      )}
    </div>
  );
}

export interface ActiveFilter { key: string; label: string; tone: Tone; onClear: () => void }

/** Aktywne filtry jako chipy — każdy zdejmowany osobno, plus „Wyczyść wszystko". */
export function ActiveFilterBar({ filters, onClearAll, shown, total }: {
  filters: ActiveFilter[]; onClearAll: () => void; shown: number; total: number;
}) {
  if (!filters.length && shown === total) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t bg-muted/20 px-3 py-2">
      {filters.map(f => (
        <span key={f.key} className={cn('inline-flex items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-[11px] font-semibold', TONE[f.tone].picked)}>
          {f.label}
          <button type="button" onClick={f.onClear} title="Zdejmij ten filtr" className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {filters.length > 1 && (
        <button type="button" onClick={onClearAll}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground">
          <FilterX className="h-3 w-3" /> Wyczyść wszystko
        </button>
      )}
      <span className="ml-auto text-xs text-muted-foreground">
        Pokazuję <b className="tabular-nums text-foreground">{shown}</b> z <span className="tabular-nums">{total}</span>
      </span>
    </div>
  );
}

// ─── Grupy ────────────────────────────────────────────────────────────────────

/** Nagłówek sekcji grupowania — klik zwija/rozwija; pasek pokazuje udział grupy w widocznych. */
export function GroupHeader({ icon: Icon, label, count, total, cls, bar, hint, collapsed, onToggle }: {
  icon: ElementType;
  label: string;
  count: number;
  total: number;
  /** Klasy chipu ikony i licznika (tło -50, tekst -700, obwódka -200). */
  cls: string;
  /** Kolor paska udziału (klasa tła). */
  bar: string;
  hint?: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const share = total ? Math.round((count / total) * 100) : 0;
  return (
    <button type="button" onClick={onToggle} aria-expanded={!collapsed}
      className="group/gh mb-2.5 flex w-full items-center gap-2.5 rounded-xl px-1 py-1 text-left transition-colors hover:bg-muted/40">
      <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg border', cls)}>
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-sm font-bold">{label}</h2>
      <span className={cn('inline-flex min-w-[1.3rem] items-center justify-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold tabular-nums', cls)}>{count}</span>
      {hint && <span className="hidden text-[11px] text-muted-foreground sm:inline">· {hint}</span>}
      <span className="ml-auto hidden items-center gap-2 sm:flex" title={`${share}% widocznych`}>
        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
          <span className={cn('block h-full rounded-full', bar)} style={{ width: `${share}%` }} />
        </span>
        <span className="w-8 text-right text-[10px] tabular-nums text-muted-foreground">{share}%</span>
      </span>
      <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover/gh:text-foreground sm:ml-0 ml-auto', collapsed && '-rotate-90')} />
    </button>
  );
}

// ─── Stan zapamiętany w przeglądarce ──────────────────────────────────────────

/** `useState` zapamiętany w localStorage; wartość spoza `allowed` (stara/obca) → `initial`. */
export function usePersistentState<T extends string>(key: string, initial: T, allowed: readonly T[]) {
  const [value, setValue] = useState<T>(() => {
    try {
      const v = localStorage.getItem(key) as T | null;
      return v && allowed.includes(v) ? v : initial;
    } catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, value); } catch { /* prywatne okno — trudno */ } }, [key, value]);
  return [value, setValue] as const;
}

/** Zwinięte sekcje grupowania — zapamiętane per strona. */
export function useCollapsedGroups(key: string) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(key) ?? '[]')); } catch { return new Set<string>(); }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify([...collapsed])); } catch { /* ignore */ } }, [key, collapsed]);
  const toggle = useCallback((k: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  }), []);
  const setAll = useCallback((keys: string[], fold: boolean) => setCollapsed(fold ? new Set(keys) : new Set()), []);
  return { isCollapsed: (k: string) => collapsed.has(k), toggle, setAll, anyCollapsed: collapsed.size > 0 };
}
